import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, Search, X, Save, Truck, RefreshCcw, Phone, Mail } from "lucide-react";
import { OpenInBrowserButton } from "@/components/dashboard/browser/OpenInBrowser";
import { useConfirm } from "@/hooks/useConfirm";
import BusinessDuplicates from "@/components/dashboard/BusinessDuplicates";
import ReceiptViewer from "@/components/dashboard/ReceiptViewer";

const emptyForm = {
  name: "", contact_name: "", phone: "", email: "", website: "",
  address: "", category: "", account_number: "", notes: "",
};

const Field = ({ label, v, onChange, testid, area, placeholder }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</label>
    {area ? (
      <textarea
        rows={3} value={v} onChange={(ev) => onChange(ev.target.value)}
        placeholder={placeholder} data-testid={testid}
        className="mt-1 w-full rounded border border-white/15 bg-white/5 px-2 py-1.5 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
      />
    ) : (
      <input
        value={v} onChange={(ev) => onChange(ev.target.value)}
        placeholder={placeholder} data-testid={testid}
        className="mt-1 h-9 w-full rounded border border-white/15 bg-white/5 px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
      />
    )}
  </div>
);

const VendorDialog = ({ open, vendor, onClose, onSaved }) => {
  const isEdit = !!vendor?.vendor_id;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(vendor ? { ...emptyForm, ...vendor } : emptyForm);
  }, [open, vendor]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/vendors/${vendor.vendor_id}`, form);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", form);
        toast.success("Vendor added");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save vendor");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="vendor-dialog">
      <div className="w-full max-w-2xl overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Vendor</p>
            <h3 className="text-lg font-bold uppercase tracking-tight">{isEdit ? "Edit vendor" : "Add vendor"}</h3>
          </div>
          <button onClick={onClose} data-testid="vendor-dialog-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Company name *" v={form.name} onChange={(v) => setForm((s) => ({ ...s, name: v }))} testid="vendor-name" />
            <Field label="Category" v={form.category} onChange={(v) => setForm((s) => ({ ...s, category: v }))} testid="vendor-category" placeholder="Materials, Insulation, Rentals…" />
            <Field label="Contact name" v={form.contact_name} onChange={(v) => setForm((s) => ({ ...s, contact_name: v }))} testid="vendor-contact" />
            <Field label="Account #" v={form.account_number} onChange={(v) => setForm((s) => ({ ...s, account_number: v }))} testid="vendor-account" />
            <Field label="Phone" v={form.phone} onChange={(v) => setForm((s) => ({ ...s, phone: v }))} testid="vendor-phone" />
            <Field label="Email" v={form.email} onChange={(v) => setForm((s) => ({ ...s, email: v }))} testid="vendor-email" />
            <Field label="Website" v={form.website} onChange={(v) => setForm((s) => ({ ...s, website: v }))} testid="vendor-website" placeholder="https://…" />
            <Field label="Address" v={form.address} onChange={(v) => setForm((s) => ({ ...s, address: v }))} testid="vendor-address" />
          </div>
          <Field label="Notes" v={form.notes} onChange={(v) => setForm((s) => ({ ...s, notes: v }))} testid="vendor-notes" area />
        </div>
        <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
          <button onClick={onClose} className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70" data-testid="vendor-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50" data-testid="vendor-save">
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : (isEdit ? "Save changes" : "Add vendor")}
          </button>
        </div>
      </div>
    </div>
  );
};
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA") : "—");

/** Click a vendor card -> their whole spend history in one place. */
const VendorDetail = ({ vendorId, onClose, onEdit }) => {
  const [data, setData] = useState(null); // null = loading
  const [loadErr, setLoadErr] = useState("");
  const [receiptSrc, setReceiptSrc] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    let live = true;
    setData(null);
    setLoadErr("");
    api.get(`/vendors/${vendorId}/expenses`)
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setLoadErr(e?.response?.data?.detail || "Could not load vendor"); });
    return () => { live = false; };
  }, [vendorId]);

  if (!vendorId) return null;
  const v = data?.vendor || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="vendor-detail"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-start justify-between border-b border-white/10 px-4 py-3">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Vendor</p>
            <h3 className="truncate text-lg font-bold uppercase tracking-tight" data-testid="vendor-detail-name">{v.name || "…"}</h3>
            {v.category && <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-white/40">{v.category}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {data && <OpenInBrowserButton url={v.website} name={v.name} testid="vendor-detail-website" className="border border-white/15 bg-white/5 !text-white/70 hover:!bg-white/10 hover:!text-white" />}
            {onEdit && data && (
              <button onClick={() => onEdit(v)} data-testid="vendor-detail-edit"
                className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white">
                <Pencil className="h-4 w-4" />
              </button>
            )}
            <button onClick={onClose} data-testid="vendor-detail-close"
              className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {loadErr ? (
          <p className="p-6 text-center text-sm text-red-300" data-testid="vendor-detail-error">{loadErr}</p>
        ) : !data ? (
          <p className="p-6 text-center font-mono text-xs uppercase tracking-widest text-white/40">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 px-4 py-3">
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-center">
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Total spent</p>
                <p className="mt-1 font-mono text-lg font-semibold text-emerald-300" data-testid="vendor-detail-total">{fmtMoney(data.total_spent)}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-center">
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Expenses</p>
                <p className="mt-1 font-mono text-lg font-semibold text-white" data-testid="vendor-detail-count">{data.count}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-center">
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Projects</p>
                <p className="mt-1 font-mono text-lg font-semibold text-white" data-testid="vendor-detail-projects">{data.projects_count}</p>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
              {data.expenses.length === 0 ? (
                <p className="rounded-lg border border-dashed border-white/15 p-6 text-center text-sm text-white/50" data-testid="vendor-detail-empty">
                  No expenses recorded for this vendor yet.
                </p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-white/10">
                  {data.expenses.map((x) => (
                    <div key={x.expense_id} data-testid={`vendor-expense-row-${x.expense_id}`}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 px-3 py-2.5 text-sm last:border-b-0">
                      {x.receipt_image && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setReceiptSrc(x.receipt_image); }}
                          data-testid={`vendor-receipt-${x.expense_id}`}
                          title="View receipt"
                          className="shrink-0 overflow-hidden rounded border border-white/15 hover:border-emerald-400/60"
                        >
                          <img src={x.receipt_image} alt="Receipt" className="h-10 w-10 object-cover" />
                        </button>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                          {fmtDate(x.created_at)}
                          {(x.project_po || x.project_name) ? ` · ${[x.project_po, x.project_name].filter(Boolean).join(" — ")}` : ""}
                          {x.owner ? ` · ${x.owner}` : ""}
                        </p>
                        <p className="truncate font-medium text-white/90">{x.name}</p>
                      </div>
                      <p className="shrink-0 font-mono text-sm text-white">{fmtMoney(x.total)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <ReceiptViewer src={receiptSrc} onClose={() => setReceiptSrc(null)} />
    </div>
  );
};


const VendorsTab = () => {
  const { user } = useAuth();
  // Duplicate-merge is an admin/owner tool — don't mount it (or fire its API
  // call) for employees, or they'd get a 403 toast just for opening Vendors.
  const canDedupe = ["owner", "superadmin", "admin"].includes(user?.role) || !!user?.manage_users;
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [query, setQuery] = useState("");
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendors");
      setVendors(data || []);
    } catch { toast.error("Could not load vendors"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter((v) => [v.name, v.contact_name, v.category, v.email, v.phone].join(" ").toLowerCase().includes(q));
  }, [vendors, query]);

  const startAdd = () => { setEditing(null); setDialogOpen(true); };
  const startEdit = (v) => { setEditing(v); setDialogOpen(true); };

  const [syncing, setSyncing] = useState(false);
  const syncQbo = async () => {
    const ok = await confirm({
      title: "Pull vendors from QuickBooks?",
      description: "One-way import: vendors on file in QuickBooks are added here. Existing vendors are never duplicated and nothing is written back to QuickBooks.",
      confirmLabel: "Sync",
    });
    if (!ok) return;
    setSyncing(true);
    try {
      const r = await api.post("/vendors/sync-qbo");
      const d = r.data;
      toast.success(`QuickBooks sync: ${d.added} new vendor${d.added === 1 ? "" : "s"} · ${d.skipped_existing} already on file`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "QuickBooks sync failed");
    } finally { setSyncing(false); }
  };
  const remove = async (v) => {
    const ok = await confirm({ title: `Delete "${v.name}"?`, confirmLabel: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/vendors/${v.vendor_id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="bg-[#f4f5f8] p-4 sm:p-6" data-testid="vendors-tab">
      <div className="mx-auto mb-6 max-w-6xl">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#4f6fe8]">Vendors</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Suppliers</h1>
        <p className="mt-1 font-mono text-[11px] text-gray-400">{vendors.length} on file</p>
      </div>
      <div className="mx-auto max-w-6xl rounded-lg bg-white shadow-sm">
        {/* toolbar */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={query} onChange={(ev) => setQuery(ev.target.value)}
              placeholder="Search"
              data-testid="vendors-search"
              className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-[#2ca01c] focus:outline-none focus:ring-1 focus:ring-[#2ca01c]"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={syncQbo}
              disabled={syncing}
              data-testid="vendors-sync-qbo"
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-emerald-600 hover:border-emerald-500/60 disabled:opacity-50 dark:text-emerald-300"
              title="Pull vendors from QuickBooks (read-only — nothing is sent to QuickBooks)"
            >
              <RefreshCcw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync QBO"}
            </button>
            <button
              type="button"
              onClick={startAdd}
              data-testid="vendors-add-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#1a3fd1]/85"
            >
              <Plus className="h-4 w-4" /> Add vendor
            </button>
          </div>
        </div>

        {/* table header (desktop) */}
        <div className="hidden grid-cols-[2fr_1.6fr_1.2fr_1.6fr_0.7fr] items-center gap-3 border-b border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 md:grid">
          <span>Name</span>
          <span>Contact</span>
          <span>Phone</span>
          <span>Email</span>
          <span className="text-right">Action</span>
        </div>

        {/* rows */}
        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400">Loading vendors…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">
            {vendors.length === 0 ? "No vendors yet. Add your first supplier." : "No matches."}
          </div>
        ) : (
          <div className="max-h-[62vh] overflow-y-auto">
            {filtered.map((v) => (
              <div
                key={v.vendor_id}
                data-testid="vendor-row"
                onClick={() => setDetailId(v.vendor_id)}
                className="group flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 hover:bg-[#f0f7ff] md:grid md:grid-cols-[2fr_1.6fr_1.2fr_1.6fr_0.7fr] md:gap-3 md:px-4 md:py-3"
              >
                {/* Mobile: compact single line (icon + name + subtitle). Desktop: name cell. */}
                <span className="flex min-w-0 flex-1 items-center gap-2 md:contents">
                  <Truck className="h-3.5 w-3.5 shrink-0 text-[#2c6eeb] md:hidden" />
                  <span className="flex min-w-0 flex-col md:contents">
                    <span className="truncate text-sm font-semibold text-[#2c6eeb] group-hover:underline">{v.name}</span>
                    <span className="truncate font-mono text-[10px] uppercase tracking-widest text-gray-400 md:hidden">
                      {[v.category, v.contact_name].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </span>
                </span>
                <span className="hidden truncate text-sm text-gray-600 md:block">
                  {v.contact_name || ""}{v.category ? (v.contact_name ? ` · ${v.category}` : v.category) : ""}
                </span>
                <span className="hidden truncate text-sm text-gray-600 md:block">
                  {v.phone && (
                    <a href={`tel:${v.phone}`} onClick={(e) => e.stopPropagation()} data-testid={`vendor-phone-${v.vendor_id}`} className="hover:text-[#2c6eeb] hover:underline">
                      {v.phone}
                    </a>
                  )}
                </span>
                <span className="hidden truncate text-sm text-gray-600 md:block">
                  {v.email && (
                    <a href={`mailto:${v.email}`} onClick={(e) => e.stopPropagation()} data-testid={`vendor-email-${v.vendor_id}`} className="hover:text-[#2c6eeb] hover:underline">
                      {v.email}
                    </a>
                  )}
                </span>
                <div className="flex shrink-0 items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                  <OpenInBrowserButton url={v.website} name={v.name} testid={`vendor-website-${v.vendor_id}`} />
                  {/* mobile quick actions — call/email straight from the row */}
                  {v.phone && (
                    <a
                      href={`tel:${v.phone}`}
                      data-testid={`vendor-call-mobile-${v.vendor_id}`}
                      aria-label={`Call ${v.name}`}
                      className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50 md:hidden"
                    >
                      <Phone className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {v.email && (
                    <a
                      href={`mailto:${v.email}`}
                      data-testid={`vendor-email-mobile-${v.vendor_id}`}
                      aria-label={`Email ${v.name}`}
                      className="rounded p-1.5 text-sky-600 hover:bg-sky-50 md:hidden"
                    >
                      <Mail className="h-3.5 w-3.5" />
                    </a>
                  )}
                  <button
                    onClick={() => startEdit(v)}
                    data-testid={`vendor-edit-${v.vendor_id}`}
                    aria-label="Edit vendor"
                    className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-[#2c6eeb] md:opacity-0 md:group-hover:opacity-100"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => remove(v)}
                    data-testid={`vendor-delete-${v.vendor_id}`}
                    aria-label="Delete vendor"
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 md:opacity-0 md:group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 text-xs text-gray-400">
          <span>{filtered.length} vendors</span>
        </div>
      </div>

      <VendorDialog
        open={dialogOpen}
        vendor={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
      />

      <VendorDetail
        vendorId={detailId}
        onClose={() => setDetailId(null)}
        onEdit={(v) => { setDetailId(null); startEdit(v); }}
      />

      {canDedupe && <BusinessDuplicates onChanged={load} />}
    </div>
  );
};

export default VendorsTab;
